import { cva } from "class-variance-authority";
import React, { PropsWithChildren, forwardRef } from "react";
import { Link as ReactLink } from "react-router-dom";

import { cn } from "../../../lib/utils";

export interface LinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
	/**
	 * The URL to link to
	 */
	href: string;
	/**
	 * Optional class name to apply to the link
	 */
	dangerouslySetClassName?: string;
	/**
	 * If true, the link will force a hard reload of the page
	 */
	forceHardReload?: boolean;
	/**
	 * If true, the link will open in a new tab
	 */
	isExternal?: boolean;
	/**
	 * The underline behavior of the link
	 * @default "always"
	 */
	underline?: "hover" | "always" | "never";
	/**
	 * Optional test ID for the link
	 */
	"data-testid"?: string;
}

const linkVariants = cva("cursor-pointer text-primary-5", {
	variants: {
		underline: {
			hover: "no-underline hover:underline",
			always: "underline",
			never: "no-underline",
		},
	},
	defaultVariants: {
		underline: "always",
	},
});

/**
 * The Link component is a wrapper around React Router's Link component that allows for more control over the link's behavior such as opening in a new tab or forcing a hard reload.
 */
const Link = forwardRef<HTMLAnchorElement, PropsWithChildren<LinkProps>>((props, ref) => {
	const {
		href,
		dangerouslySetClassName,
		forceHardReload,
		isExternal,
		underline,
		children,
		"data-testid": dataTestId,
		...rest
	} = props;
	return (
		<ReactLink
			ref={ref}
			className={cn(linkVariants({ underline }), dangerouslySetClassName)}
			to={href}
			data-testid={dataTestId}
			target={isExternal ? "_blank" : undefined}
			reloadDocument={forceHardReload}
			{...rest}
		>
			{children}
		</ReactLink>
	);
});

Link.displayName = "Link";

export { Link };
